import { Button } from "@/components/ui/button";
import { resolveMediaUrl } from "@/lib/utils";
import type { PortfolioTemplate } from "./services/portfolio-api.service";

type TemplateProjectLike = {
	id?: string;
	title?: string;
	shortDescription?: string;
	thumbnailUrl?: string;
	imageUrls?: string[];
};

export interface TemplateRenderProps {
	projects: TemplateProjectLike[];
	onOpenProject?: (projectId: string) => void;
	loadingProjectId?: string | null;
	mode?: "preview" | "public";
}

type TemplateRenderer = (props: TemplateRenderProps) => React.ReactElement;

export interface PortfolioTemplateDefinition {
	id: PortfolioTemplate;
	name: string;
	description: string;
	changesSummary: string;
	MiniPreview: TemplateRenderer;
	LivePreview: TemplateRenderer;
	Section: TemplateRenderer;
}

const DEFAULT_PLACEHOLDER_CLASS =
	"aspect-[4/3] rounded-lg bg-gradient-to-br from-dashboard-border via-dashboard-divider to-dashboard-surface text-dashboard-muted";

const STUDIO_PLACEHOLDER_CLASS = "bg-dashboard-warm-100 text-dashboard-muted";

function LocalPlaceholder({
	label,
	className = DEFAULT_PLACEHOLDER_CLASS,
}: {
	label: string;
	className?: string;
}) {
	return (
		<div
			className={`grid place-items-center text-[11px] font-medium uppercase tracking-wide ${className}`}
		>
			{label}
		</div>
	);
}

function Media({
	src,
	alt,
	className,
	fallbackLabel,
	placeholderClassName,
}: {
	src?: string;
	alt: string;
	className?: string;
	fallbackLabel: string;
	placeholderClassName?: string;
}): React.ReactElement {
	const resolved = resolveMediaUrl(src);
	if (!resolved) {
		return (
			<LocalPlaceholder
				label={fallbackLabel}
				className={placeholderClassName}
			/>
		);
	}

	return (
		<img
			src={resolved}
			alt={alt}
			className={className || "aspect-[4/3] w-full rounded-lg object-cover"}
		/>
	);
}

function cardBase(project: TemplateProjectLike | undefined, index: number) {
	return {
		title: project?.title || `Project ${index + 1}`,
		shortDescription:
			project?.shortDescription ||
			"Add short description to preview layout behavior.",
		thumbnailUrl: project?.thumbnailUrl,
		id: project?.id || `preview-${index}`,
	};
}

function TemplateAMiniPreview({
	projects,
}: TemplateRenderProps): React.ReactElement {
	const items = [0, 1, 2].map((idx) => cardBase(projects[idx], idx));
	return (
		<div data-testid="template-a-mini" className="grid gap-2 sm:grid-cols-2">
			{items.map((item, idx) => (
				<div
					key={item.id}
					className={`overflow-hidden rounded-lg border bg-card ${idx === 2 ? "sm:col-span-2" : ""}`}
				>
					<Media
						src={item.thumbnailUrl}
						alt={item.title}
						fallbackLabel={`A ${idx + 1}`}
						className="h-20 w-full object-cover"
					/>
					<div className="p-2">
						<p className="truncate text-xs font-semibold">{item.title}</p>
					</div>
				</div>
			))}
		</div>
	);
}

function TemplateBMiniPreview({
	projects,
}: TemplateRenderProps): React.ReactElement {
	const hero = cardBase(projects[0], 0);
	const side1 = cardBase(projects[1], 1);
	const side2 = cardBase(projects[2], 2);

	return (
		<div data-testid="template-b-mini" className="grid grid-cols-3 gap-2">
			<div className="col-span-2 overflow-hidden rounded-lg border bg-card">
				<Media
					src={hero.thumbnailUrl}
					alt={hero.title}
					fallbackLabel="B Hero"
					className="h-24 w-full object-cover"
				/>
			</div>
			<div className="space-y-2">
				<div className="overflow-hidden rounded-lg border bg-card">
					<Media
						src={side1.thumbnailUrl}
						alt={side1.title}
						fallbackLabel="B 2"
						className="h-11 w-full object-cover"
					/>
				</div>
				<div className="overflow-hidden rounded-lg border bg-card">
					<Media
						src={side2.thumbnailUrl}
						alt={side2.title}
						fallbackLabel="B 3"
						className="h-11 w-full object-cover"
					/>
				</div>
			</div>
		</div>
	);
}

function TemplateALivePreview({
	projects,
}: TemplateRenderProps): React.ReactElement {
	const items = [0, 1, 2].map((idx) => cardBase(projects[idx], idx));
	return (
		<div
			data-testid="template-a-live"
			className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3"
		>
			{items.map((item) => (
				<div
					key={item.id}
					className="overflow-hidden rounded-xl border bg-card"
				>
					<Media
						src={item.thumbnailUrl}
						alt={item.title}
						fallbackLabel="Classic Grid"
						className="h-28 w-full object-cover"
					/>
					<div className="space-y-1 p-3">
						<p className="truncate text-sm font-semibold">{item.title}</p>
						<p className="line-clamp-2 text-xs text-muted-foreground">
							{item.shortDescription}
						</p>
					</div>
				</div>
			))}
		</div>
	);
}

function TemplateBLivePreview({
	projects,
}: TemplateRenderProps): React.ReactElement {
	const hero = cardBase(projects[0], 0);
	const side = [cardBase(projects[1], 1), cardBase(projects[2], 2)];

	return (
		<div data-testid="template-b-live" className="space-y-3">
			<div className="grid gap-3 md:grid-cols-3">
				<div className="md:col-span-2 overflow-hidden rounded-xl border bg-card">
					<Media
						src={hero.thumbnailUrl}
						alt={hero.title}
						fallbackLabel="Featured Story Hero"
						className="h-44 w-full object-cover"
					/>
					<div className="p-3">
						<p className="text-sm font-semibold">{hero.title}</p>
						<p className="line-clamp-2 text-xs text-muted-foreground">
							{hero.shortDescription}
						</p>
					</div>
				</div>
				<div className="space-y-3">
					{side.map((item) => (
						<div
							key={item.id}
							className="overflow-hidden rounded-xl border bg-card"
						>
							<Media
								src={item.thumbnailUrl}
								alt={item.title}
								fallbackLabel="Featured Story"
								className="h-20 w-full object-cover"
							/>
							<div className="p-2">
								<p className="truncate text-xs font-medium">{item.title}</p>
							</div>
						</div>
					))}
				</div>
			</div>
		</div>
	);
}

function TemplateASection({
	projects,
	onOpenProject,
	loadingProjectId,
}: TemplateRenderProps): React.ReactElement {
	if (projects.length === 0) {
		return <StudioEmptySection testId="template-a-section" />;
	}

	return (
		<div
			data-testid="template-a-section"
			className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3"
		>
			{projects.map((project, index) => {
				const item = cardBase(project, index);
				return (
					<button
						key={item.id}
						type="button"
						className="group overflow-hidden rounded-2xl border bg-card text-left shadow-sm transition hover:-translate-y-0.5 hover:shadow-lg"
						onClick={() => onOpenProject?.(project.id || "")}
						disabled={!project.id || loadingProjectId === project.id}
					>
						<div className="relative overflow-hidden">
							<Media
								src={item.thumbnailUrl}
								alt={item.title}
								fallbackLabel="Project"
								className="aspect-[4/3] w-full object-cover transition duration-300 group-hover:scale-105"
							/>
							<div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/45 to-transparent" />
						</div>
						<div className="space-y-1 p-4">
							<p className="font-semibold text-foreground">{item.title}</p>
							<p className="line-clamp-2 text-sm text-muted-foreground">
								{item.shortDescription}
							</p>
						</div>
					</button>
				);
			})}
		</div>
	);
}

function TemplateBSection({
	projects,
	onOpenProject,
	loadingProjectId,
}: TemplateRenderProps): React.ReactElement {
	if (projects.length === 0) {
		return <StudioEmptySection testId="template-b-section" />;
	}

	const hero = projects[0];
	const heroItem = cardBase(hero, 0);
	const compact = projects.slice(1, 3);
	const rest = projects.slice(3);

	return (
		<div data-testid="template-b-section" className="space-y-5">
			<div className="grid gap-4 lg:grid-cols-3">
				<button
					type="button"
					className="group overflow-hidden rounded-2xl border bg-card text-left shadow-sm transition hover:-translate-y-0.5 hover:shadow-lg lg:col-span-2"
					onClick={() => onOpenProject?.(hero?.id || "")}
					disabled={!hero?.id || loadingProjectId === hero?.id}
				>
					<div className="relative overflow-hidden">
						<Media
							src={heroItem.thumbnailUrl}
							alt={heroItem.title}
							fallbackLabel="Hero"
							className="aspect-[16/9] w-full object-cover transition duration-300 group-hover:scale-105"
						/>
						<div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/55 to-transparent" />
						<p className="absolute bottom-3 left-3 text-sm font-semibold text-dashboard-on-fill">
							Featured Project
						</p>
					</div>
					<div className="space-y-1 p-4">
						<p className="text-base font-semibold">{heroItem.title}</p>
						<p className="line-clamp-2 text-sm text-muted-foreground">
							{heroItem.shortDescription}
						</p>
					</div>
				</button>

				<div className="space-y-4">
					{compact.map((project, idx) => {
						const item = cardBase(project, idx + 1);
						return (
							<button
								key={item.id}
								type="button"
								className="group w-full overflow-hidden rounded-2xl border bg-card text-left shadow-sm transition hover:shadow-md"
								onClick={() => onOpenProject?.(project.id || "")}
								disabled={!project.id || loadingProjectId === project.id}
							>
								<Media
									src={item.thumbnailUrl}
									alt={item.title}
									fallbackLabel={`Card ${idx + 2}`}
									className="h-28 w-full object-cover"
								/>
								<div className="p-3">
									<p className="truncate text-sm font-semibold">{item.title}</p>
								</div>
							</button>
						);
					})}
				</div>
			</div>

			{rest.length > 0 ? (
				<div className="space-y-4">
					{rest.map((project, idx) => {
						const item = cardBase(project, idx + 3);
						const wide = idx % 2 === 0;
						return (
							<button
								key={item.id}
								type="button"
								className={`group grid w-full gap-0 overflow-hidden rounded-2xl border bg-card text-left shadow-sm transition hover:shadow-md ${wide ? "md:grid-cols-[1.4fr_1fr]" : "md:grid-cols-[1fr_1.4fr]"}`}
								onClick={() => onOpenProject?.(project.id || "")}
								disabled={!project.id || loadingProjectId === project.id}
							>
								{wide ? (
									<>
										<Media
											src={item.thumbnailUrl}
											alt={item.title}
											fallbackLabel="Wide"
											className="h-52 w-full object-cover"
										/>
										<div className="flex flex-col justify-center p-4">
											<p className="text-base font-semibold">{item.title}</p>
											<p className="mt-1 line-clamp-3 text-sm text-muted-foreground">
												{item.shortDescription}
											</p>
										</div>
									</>
								) : (
									<>
										<div className="flex flex-col justify-center p-4">
											<p className="text-base font-semibold">{item.title}</p>
											<p className="mt-1 line-clamp-3 text-sm text-muted-foreground">
												{item.shortDescription}
											</p>
										</div>
										<Media
											src={item.thumbnailUrl}
											alt={item.title}
											fallbackLabel="Wide"
											className="h-52 w-full object-cover"
										/>
									</>
								)}
							</button>
						);
					})}
				</div>
			) : null}
		</div>
	);
}

function StudioEmptySection({
	testId,
}: {
	testId: string;
}): React.ReactElement {
	return (
		<div
			data-testid={testId}
			className="rounded-xl border border-dashboard-border bg-dashboard-surface p-6 text-sm text-dashboard-muted"
		>
			No projects yet.
		</div>
	);
}

function MagazineSpreadMiniPreview({
	projects,
}: TemplateRenderProps): React.ReactElement {
	const hero = cardBase(projects[0], 0);
	const rows = [cardBase(projects[1], 1), cardBase(projects[2], 2)];

	return (
		<div data-testid="template-c-mini" className="space-y-1.5">
			<Media
				src={hero.thumbnailUrl}
				alt={hero.title}
				fallbackLabel="C Hero"
				className="h-14 w-full rounded-md object-cover"
				placeholderClassName={`h-14 rounded-md ${STUDIO_PLACEHOLDER_CLASS}`}
			/>
			{rows.map((item, idx) => (
				<div
					key={item.id}
					className={`flex items-center gap-1.5 ${idx % 2 === 1 ? "flex-row-reverse" : ""}`}
				>
					<div className="w-1/2 shrink-0">
						<Media
							src={item.thumbnailUrl}
							alt={item.title}
							fallbackLabel={`C ${idx + 2}`}
							className="h-9 w-full rounded-md object-cover"
							placeholderClassName={`h-9 rounded-md ${STUDIO_PLACEHOLDER_CLASS}`}
						/>
					</div>
					<p className="min-w-0 flex-1 truncate text-[11px] font-semibold text-dashboard-text">
						{item.title}
					</p>
				</div>
			))}
		</div>
	);
}

function MagazineSpreadLivePreview({
	projects,
}: TemplateRenderProps): React.ReactElement {
	const hero = cardBase(projects[0], 0);
	const rows = [cardBase(projects[1], 1), cardBase(projects[2], 2)];

	return (
		<div data-testid="template-c-live" className="space-y-3 bg-dashboard-bg">
			<div className="relative overflow-hidden">
				<Media
					src={hero.thumbnailUrl}
					alt={hero.title}
					fallbackLabel="Magazine Spread Hero"
					className="h-40 w-full object-cover"
					placeholderClassName={`h-40 ${STUDIO_PLACEHOLDER_CLASS}`}
				/>
				<div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/60 to-transparent p-3">
					<p className="font-heading text-base font-semibold text-dashboard-on-fill">
						{hero.title}
					</p>
				</div>
			</div>
			{rows.map((item, idx) => (
				<div
					key={item.id}
					className="grid items-center gap-3 px-3 pb-3 sm:grid-cols-2"
				>
					<div className={idx % 2 === 1 ? "sm:order-2" : ""}>
						<Media
							src={item.thumbnailUrl}
							alt={item.title}
							fallbackLabel="Magazine Spread"
							className="h-24 w-full rounded-lg object-cover"
							placeholderClassName={`h-24 rounded-lg ${STUDIO_PLACEHOLDER_CLASS}`}
						/>
					</div>
					<div>
						<p className="font-heading text-sm font-semibold text-dashboard-text">
							{item.title}
						</p>
						<p className="line-clamp-2 text-xs text-dashboard-muted">
							{item.shortDescription}
						</p>
					</div>
				</div>
			))}
		</div>
	);
}

function MagazineSpreadSection({
	projects,
	onOpenProject,
	loadingProjectId,
}: TemplateRenderProps): React.ReactElement {
	if (projects.length === 0) {
		return <StudioEmptySection testId="template-c-section" />;
	}

	const [hero, ...rows] = projects;
	const heroItem = cardBase(hero, 0);

	return (
		<div data-testid="template-c-section" className="space-y-10">
			<button
				type="button"
				className="group relative block w-full overflow-hidden rounded-2xl text-left"
				onClick={() => onOpenProject?.(hero.id || "")}
				disabled={!hero.id || loadingProjectId === hero.id}
			>
				<Media
					src={heroItem.thumbnailUrl}
					alt={heroItem.title}
					fallbackLabel="Hero"
					className="aspect-[4/3] w-full object-cover transition duration-500 group-hover:scale-[1.02] sm:aspect-[21/9]"
					placeholderClassName={`aspect-[4/3] sm:aspect-[21/9] ${STUDIO_PLACEHOLDER_CLASS}`}
				/>
				<div className="pointer-events-none absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/65 to-transparent p-5 sm:p-8">
					<p className="font-heading text-2xl font-semibold text-dashboard-on-fill sm:text-3xl">
						{heroItem.title}
					</p>
					<p className="mt-2 line-clamp-2 max-w-2xl text-sm text-dashboard-on-fill/85">
						{heroItem.shortDescription}
					</p>
				</div>
			</button>

			{rows.map((project, idx) => {
				const item = cardBase(project, idx + 1);
				const imageRight = idx % 2 === 1;
				return (
					<button
						key={item.id}
						type="button"
						className="group grid w-full items-center gap-5 text-left md:grid-cols-2 md:gap-10"
						onClick={() => onOpenProject?.(project.id || "")}
						disabled={!project.id || loadingProjectId === project.id}
					>
						<div
							className={`overflow-hidden rounded-2xl ${imageRight ? "md:order-2" : ""}`}
						>
							<Media
								src={item.thumbnailUrl}
								alt={item.title}
								fallbackLabel="Project"
								className="aspect-[4/3] w-full object-cover transition duration-500 group-hover:scale-[1.03]"
								placeholderClassName={`aspect-[4/3] ${STUDIO_PLACEHOLDER_CLASS}`}
							/>
						</div>
						<div className="space-y-2">
							<p className="font-mono text-xs font-semibold tracking-[0.18em] text-dashboard-accent-dark">
								{String(idx + 2).padStart(2, "0")}
							</p>
							<p className="font-heading text-xl font-semibold text-dashboard-text">
								{item.title}
							</p>
							<p className="line-clamp-4 text-sm leading-relaxed text-dashboard-muted">
								{item.shortDescription}
							</p>
						</div>
					</button>
				);
			})}
		</div>
	);
}

const GALLERY_PLACEHOLDER_RATIOS = [
	"aspect-[3/4]",
	"aspect-square",
	"aspect-[4/3]",
];

function galleryPlaceholderClass(index: number): string {
	const ratio =
		GALLERY_PLACEHOLDER_RATIOS[index % GALLERY_PLACEHOLDER_RATIOS.length];
	return `${ratio} ${STUDIO_PLACEHOLDER_CLASS}`;
}

function GalleryWallMiniPreview({
	projects,
}: TemplateRenderProps): React.ReactElement {
	const items = [0, 1, 2, 3, 4, 5].map((idx) => cardBase(projects[idx], idx));
	return (
		<div data-testid="template-d-mini" className="columns-3 gap-1.5">
			{items.map((item, idx) => (
				<div key={item.id} className="mb-1.5 break-inside-avoid">
					<Media
						src={item.thumbnailUrl}
						alt={item.title}
						fallbackLabel={`D ${idx + 1}`}
						className="w-full rounded-md"
						placeholderClassName={`rounded-md ${galleryPlaceholderClass(idx)}`}
					/>
				</div>
			))}
		</div>
	);
}

function GalleryWallLivePreview({
	projects,
}: TemplateRenderProps): React.ReactElement {
	const items = [0, 1, 2, 3, 4].map((idx) => cardBase(projects[idx], idx));
	return (
		<div
			data-testid="template-d-live"
			className="gap-3 bg-dashboard-bg p-3 sm:columns-2 lg:columns-3"
		>
			{items.map((item, idx) => (
				<figure key={item.id} className="mb-3 break-inside-avoid">
					<Media
						src={item.thumbnailUrl}
						alt={item.title}
						fallbackLabel="Gallery Wall"
						className="w-full rounded-lg"
						placeholderClassName={`rounded-lg ${galleryPlaceholderClass(idx)}`}
					/>
					<figcaption className="mt-1 truncate text-[11px] text-dashboard-muted">
						{item.title}
					</figcaption>
				</figure>
			))}
		</div>
	);
}

function GalleryWallSection({
	projects,
	onOpenProject,
	loadingProjectId,
}: TemplateRenderProps): React.ReactElement {
	if (projects.length === 0) {
		return <StudioEmptySection testId="template-d-section" />;
	}

	return (
		<div
			data-testid="template-d-section"
			className="gap-4 sm:columns-2 lg:columns-3"
		>
			{projects.map((project, index) => {
				const item = cardBase(project, index);
				return (
					<button
						key={item.id}
						type="button"
						className="group mb-4 block w-full break-inside-avoid overflow-hidden rounded-xl border border-dashboard-border bg-dashboard-surface text-left"
						onClick={() => onOpenProject?.(project.id || "")}
						disabled={!project.id || loadingProjectId === project.id}
					>
						<Media
							src={item.thumbnailUrl}
							alt={item.title}
							fallbackLabel="Project"
							className="w-full transition duration-500 group-hover:opacity-90"
							placeholderClassName={galleryPlaceholderClass(index)}
						/>
						<p className="px-3 py-2 text-sm font-medium text-dashboard-text">
							{item.title}
						</p>
					</button>
				);
			})}
		</div>
	);
}

const TEMPLATE_REGISTRY: Record<
	PortfolioTemplate,
	PortfolioTemplateDefinition
> = {
	template_a: {
		id: "template_a",
		name: "Classic Grid",
		description: "Balanced grid with equal project emphasis.",
		changesSummary: "Uniform cards, quick browsing, consistent image rhythm.",
		MiniPreview: TemplateAMiniPreview,
		LivePreview: TemplateALivePreview,
		Section: TemplateASection,
	},
	template_b: {
		id: "template_b",
		name: "Featured Story",
		description: "Editorial layout with hero-first storytelling.",
		changesSummary:
			"Featured hero block + supporting cards + alternating rows.",
		MiniPreview: TemplateBMiniPreview,
		LivePreview: TemplateBLivePreview,
		Section: TemplateBSection,
	},
	template_c: {
		id: "template_c",
		name: "Magazine Spread",
		description: "Full-width hero with alternating image and text rows.",
		changesSummary:
			"Full-width hero, then each project as a numbered image-and-text row.",
		MiniPreview: MagazineSpreadMiniPreview,
		LivePreview: MagazineSpreadLivePreview,
		Section: MagazineSpreadSection,
	},
	template_d: {
		id: "template_d",
		name: "Gallery Wall",
		description: "Masonry wall of photos with minimal text.",
		changesSummary:
			"Photos at their own proportions in staggered columns, titles only.",
		MiniPreview: GalleryWallMiniPreview,
		LivePreview: GalleryWallLivePreview,
		Section: GalleryWallSection,
	},
};

export const PORTFOLIO_TEMPLATES: PortfolioTemplateDefinition[] =
	Object.values(TEMPLATE_REGISTRY);

/** Looks a template up by id, falling back to `template_a` for an unknown id. */
export function getPortfolioTemplate(
	template: PortfolioTemplate,
): PortfolioTemplateDefinition {
	return TEMPLATE_REGISTRY[template] ?? TEMPLATE_REGISTRY.template_a;
}

export function renderMiniTemplatePreview(
	template: PortfolioTemplate,
	projects: TemplateProjectLike[],
): React.ReactElement {
	const { MiniPreview } = getPortfolioTemplate(template);
	return <MiniPreview projects={projects} mode="preview" />;
}

export function renderLiveTemplatePreview(
	template: PortfolioTemplate,
	projects: TemplateProjectLike[],
): React.ReactElement {
	const { LivePreview } = getPortfolioTemplate(template);
	return <LivePreview projects={projects} mode="preview" />;
}

export function renderPublicTemplateSection(
	template: PortfolioTemplate,
	projects: TemplateProjectLike[],
	onOpenProject: (projectId: string) => void,
	loadingProjectId: string | null,
): React.ReactElement {
	const { Section } = getPortfolioTemplate(template);
	return (
		<Section
			projects={projects}
			mode="public"
			onOpenProject={onOpenProject}
			loadingProjectId={loadingProjectId}
		/>
	);
}

export function SelectedTemplateBadge({
	template,
}: {
	template: PortfolioTemplate;
}): React.ReactElement {
	const definition = getPortfolioTemplate(template);

	return (
		<div className="inline-flex items-center gap-2 rounded-full border bg-muted/30 px-3 py-1 text-xs">
			<span className="font-semibold">Selected Template:</span>
			<span>{definition.name}</span>
		</div>
	);
}

export function TemplateDeltaNotice({
	selectedTemplate,
	savedTemplate,
}: {
	selectedTemplate: PortfolioTemplate;
	savedTemplate: PortfolioTemplate;
}): React.ReactElement | null {
	if (selectedTemplate === savedTemplate) return null;

	const definition = getPortfolioTemplate(selectedTemplate);

	return (
		<div className="rounded-lg border border-dashboard-warning-border bg-dashboard-warning-bg-soft p-3 text-xs text-dashboard-warning-strong">
			<p className="font-semibold">Template change not saved yet</p>
			<p className="mt-1">
				You selected {definition.name}. Click Save Portfolio to apply this
				template to your public page.
			</p>
		</div>
	);
}

export function ViewLayoutHint({
	template,
}: {
	template: PortfolioTemplate;
}): React.ReactElement {
	const definition = getPortfolioTemplate(template);
	return (
		<p className="text-xs text-muted-foreground">{definition.changesSummary}</p>
	);
}

export function ViewProjectButton({
	onClick,
}: {
	onClick: () => void;
}): React.ReactElement {
	return (
		<Button type="button" size="sm" variant="outline" onClick={onClick}>
			View Project
		</Button>
	);
}
